/**
 * LES GLYPHES, dessinés ici, un par un.
 *
 * Aucune bibliothèque d'icônes (design §1.3) — et ce n'est pas une coquetterie : une icône de
 * bibliothèque apporte avec elle le vocabulaire visuel d'un tableau de bord, et Destinia n'est pas
 * un tableau de bord. Ce sont des signes de manuscrit : tracés à la plume, un seul trait, pas de
 * remplissage sauf là où l'encre doit être pleine.
 *
 * CHAQUE GLYPHE A UN NOM ÉCRIT À CÔTÉ DE LUI dans l'interface : la couleur et la forme ne portent
 * jamais seules une information (accessibilité §5). Ici, `titre` alimente le lecteur d'écran ;
 * l'étiquette visible, elle, est de la responsabilité de l'appelant.
 */

export type NomDeGlyphe =
  | "vie"
  | "essence"
  | "faim"
  | "fatigue"
  | "horloge"
  | "nuit"
  | "jour"
  | "or"
  | "poids"
  | "sac"
  | "ancre"
  | "verrou"
  | "des"
  | "epee"
  | "bouclier"
  | "fuite"
  | "crane"
  | "plume"
  | "saignement"
  | "poison"
  | "etourdi"
  | "aveugle"
  | "terrifie"
  | "entrave"
  | "carte"
  | "lire"
  | "silence"
  | "croix"
  | "suite"
  | "revers"
  /* La fouille (§3.6) et la carte annotée (§4.2). Douze signes de plus, dessinés comme les autres :
     une bibliothèque d'icônes aurait apporté ici son coffre au trésor de jeu vidéo. */
  | "coffre"
  | "cachette"
  | "main"
  | "monstre"
  | "clef"
  | "pnj"
  | "danger"
  | "ressource"
  | "enigme"
  | "revisiter"
  | "lampe"
  | "lettre";

/** Un tracé, et s'il est plein. Les coordonnées vivent dans un carré de 24. */
const TRACES: Record<NomDeGlyphe, { d: string; plein?: boolean }> = {
  // Un cœur anatomique de manuscrit, pas un cœur de carte à jouer.
  vie: { d: "M12 20c-4-3.2-7-6-7-9.4A4 4 0 0 1 12 8a4 4 0 0 1 7 2.6c0 3.4-3 6.2-7 9.4z" },
  // Une goutte d'essence, avec son reflet.
  essence: { d: "M12 3.5c3 4.2 5 7 5 9.4a5 5 0 0 1-10 0c0-2.4 2-5.2 5-9.4zM10 13.5a2 2 0 0 0 2 2" },
  // Un quignon de pain entamé.
  faim: { d: "M4.5 13c0-3 3-5.5 7.5-5.5s7.5 2.5 7.5 5.5-3 4-7.5 4-7.5-1-7.5-4zM9 10.5v5M14 10.5v5" },
  // Une paupière lourde : la fatigue se lit sur le visage.
  fatigue: { d: "M3.5 12.5c3-4 14-4 17 0M7 14.5l-1.5 2M12 15.5v2.5M17 14.5l1.5 2" },
  // Un sablier : le temps du jeu est un entier d'heures, il coule quand même.
  horloge: { d: "M7 4h10M7 20h10M8 4c0 4 4 5 4 8s-4 4-4 8M16 4c0 4-4 5-4 8s4 4 4 8" },
  nuit: { d: "M17.5 15.5A7 7 0 0 1 9 6.2a7.6 7.6 0 1 0 8.5 9.3z" },
  jour: { d: "M12 6.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zM12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" },
  or: { d: "M12 4.5a7.5 7.5 0 1 1 0 15 7.5 7.5 0 0 1 0-15zM12 8v8M9.5 10h5M9.5 14h5" },
  poids: { d: "M6 9h12l2 10H4zM10 9V6.5a2 2 0 1 1 4 0V9" },
  sac: { d: "M5 9h14l-1.5 11h-11zM9 9V6.5a3 3 0 0 1 6 0V9M9.5 13h5" },
  // L'ancre du mode Chronique : c'est là qu'on revient.
  ancre: { d: "M12 7.5v12M8.5 11h7M12 4a2 2 0 1 1 0 4 2 2 0 0 1 0-4M5 15a7 7 0 0 0 14 0" },
  verrou: { d: "M6.5 11h11v8h-11zM9 11V8a3 3 0 0 1 6 0v3M12 14v2.5" },
  des: { d: "M12 3l8 4.6v8.8L12 21l-8-4.6V7.6zM12 3v18M4 7.6l8 4.6 8-4.6" },
  epee: { d: "M19.5 4.5L10 14M6 18l-1.5 1.5M7 15l2 2M5 13l6 6-3 1.5-4.5-4.5z" },
  bouclier: { d: "M12 3.5l7 2.5v6c0 4-3 7-7 8.5-4-1.5-7-4.5-7-8.5V6z" },
  fuite: { d: "M4 12h11M11 7.5L15.5 12 11 16.5M18.5 5v14" },
  crane: { d: "M12 3.5c4.4 0 7.5 3 7.5 7 0 2.4-1.2 4-2.5 5v3h-10v-3c-1.3-1-2.5-2.6-2.5-5 0-4 3.1-7 7.5-7zM9.5 11.5a1.6 1.6 0 1 0 0-.1M14.5 11.5a1.6 1.6 0 1 0 0-.1M12 14v2" },
  plume: { d: "M4 20c6-1 11-4 14-9 1.5-2.5 2-5 2-7-2.5.5-5 1.5-7.5 3.5C9 10.5 6 15 4 20zM7 17l5-5" },
  saignement: { d: "M12 4c2.8 4 4.6 6.7 4.6 9a4.6 4.6 0 0 1-9.2 0c0-2.3 1.8-5 4.6-9z", plein: true },
  poison: { d: "M9 3h6M10 3v3.5L6.5 16a3.5 3.5 0 0 0 3.2 5h4.6a3.5 3.5 0 0 0 3.2-5L14 6.5V3M7.5 13h9" },
  etourdi: { d: "M6 7l4 3-4 3M18 7l-4 3 4 3M8 18h8" },
  aveugle: { d: "M3 12s3.6-5.5 9-5.5c1.4 0 2.7.4 3.8 1M21 12s-3.6 5.5-9 5.5c-1.4 0-2.7-.4-3.8-1M4 4l16 16" },
  terrifie: { d: "M12 3.5c3 3.5 4.5 6 4.5 8.5a4.5 4.5 0 0 1-9 0c0-2.5 1.5-5 4.5-8.5zM12 21v-3" },
  entrave: { d: "M7 8a3 3 0 1 1 0 6 3 3 0 0 1 0-6zM17 10a3 3 0 1 1 0 6 3 3 0 0 1 0-6zM9.5 11.5l5 2" },
  // Une carte pliée en trois panneaux, comme on la sort d'une poche — pas une épingle sur un
  // planisphère : le §design refuse le vocabulaire de la carte routière.
  carte: { d: "M3 6.5l6-2.5v13l-6 2.5zM9 4l6 2.5v13L9 17zM15 6.5l6-2.5v13l-6 2.5" },
  // La voix : un bec de plume qui s'ouvre, et deux ondes. Un triangle de lecteur ferait « player ».
  lire: { d: "M6 9.5h3l4.5-4v13L9 14.5H6zM17 9a4.5 4.5 0 0 1 0 6M19.5 6.5a8 8 0 0 1 0 11" },
  // La même, muette : les ondes barrées.
  silence: { d: "M6 9.5h3l4.5-4v13L9 14.5H6zM16.5 10l5 4M21.5 10l-5 4" },
  croix: { d: "M6 6l12 12M18 6L6 18" },
  suite: { d: "M9 5l7 7-7 7" },
  revers: { d: "M15 5l-7 7 7 7" },

  /* --- Ce qu'on fouille, et ce qu'on note sur la carte ------------------------------ */

  // Une malle à couvercle bombé, cerclée de deux ferrures.
  coffre: { d: "M4 10.5h16V19H4zM4 10.5c0-3.2 3.6-5.5 8-5.5s8 2.3 8 5.5M12 10.5V19M10 13.5h4" },
  // Un mur dont une pierre a été descellée : elle sort du plan, et c'est tout le sujet.
  cachette: { d: "M3.5 6.5h17v11h-17zM3.5 12h5.5M15.5 12h5M10 7.2l5 1.1v6.4l-5-1.1z" },
  // Une main qui prend. Quatre doigts, un pouce, et rien d'autre.
  main: { d: "M8 20.5v-6l-2-3a1.5 1.5 0 0 1 2.5-1.7L10 11.5V5.4a1.4 1.4 0 0 1 2.8 0v5M12.8 10.4V4.2a1.4 1.4 0 0 1 2.8 0v6.2M15.6 10.4V6.4a1.4 1.4 0 0 1 2.8 0V14c0 3.6-2.2 6.5-5.6 6.5H8" },
  // Une empreinte à trois griffes : ce qui est passé par là n'était pas un homme.
  monstre: { d: "M6.5 4.5c1.4 2.6 1.9 4.8 1.4 7M12 3.5c1 3 1 5.3 0 7.5M17.5 4.5c-1.4 2.6-1.9 4.8-1.4 7M5.5 15c0 3.6 3 5.5 6.5 5.5s6.5-1.9 6.5-5.5c0-1.9-2-2.8-6.5-2.8s-6.5.9-6.5 2.8z" },
  // Une clef à panneton, l'anneau à gauche.
  clef: { d: "M8.5 15.5a4 4 0 1 1 0-8 4 4 0 0 1 0 8zM12.5 11.5H21M18.5 11.5V15M15.5 11.5v2.5" },
  // Quelqu'un : une tête, des épaules, et pas de visage — ce n'est pas un portrait.
  pnj: { d: "M12 4.5a3.2 3.2 0 1 1 0 6.4 3.2 3.2 0 0 1 0-6.4zM5.5 20.5c0-4 2.9-7 6.5-7s6.5 3 6.5 7" },
  // Le triangle du danger, tracé à la main, avec sa barre et son point.
  danger: { d: "M12 4L21 19.5H3zM12 10v4.4M12 16.9v.3" },
  // Une gerbe : ce qui pousse et qu'on emporte.
  ressource: { d: "M12 20.5V9.5M12 9.5c-3-1-4.6-3.6-4-6.7 3 .5 4.6 3.2 4 6.7zM12 9.5c3-1 4.6-3.6 4-6.7-3 .5-4.6 3.2-4 6.7zM6.5 20.5h11" },
  // Un point d'interrogation manuscrit : ce qu'on n'a pas résolu.
  enigme: { d: "M8.3 8.6a3.7 3.7 0 1 1 5.7 3.1c-1.4 1-2 1.8-2 3.3M12 18.7v.4" },
  // La flèche qui revient : à revoir.
  revisiter: { d: "M20 12a8 8 0 1 1-2.9-6.2M20.5 4v5.2h-5.2" },
  // Une lanterne : la lampe du pupitre, celle qu'on éteint pour la veillée.
  lampe: { d: "M9 4h6M10.2 4v2.6C8.6 7.6 7.6 9.2 7.6 11.2V19h8.8v-7.8c0-2-1-3.6-2.6-4.6V4M12 10.5v5" },
  // Un A gravé : la taille du texte, et rien d'autre.
  lettre: { d: "M5 19.5L12 4.5l7 15M8 14.5h8" }
};

/**
 * LE TRACÉ SEUL, pour qui dessine DANS un SVG qui existe déjà — la carte pose ses marqueurs dans son
 * propre repère (§4.2), et un `<svg>` imbriqué y apporterait ses coordonnées, sa taille et ses
 * surprises de rendu. Elle prend donc le chemin et le place elle-même. La table reste privée : un
 * seul endroit dessine, et c'est ici.
 */
export function traceDe(nom: NomDeGlyphe): { d: string; plein?: boolean } {
  return TRACES[nom];
}

interface Props {
  nom: NomDeGlyphe;
  taille?: number;
  titre?: string;
  couleur?: string;
}

export function Glyphe({ nom, taille = 18, titre, couleur }: Props) {
  const trace = TRACES[nom];
  return (
    <svg
      className={trace.plein ? "glyphe glyphe--plein" : "glyphe"}
      width={taille}
      height={taille}
      viewBox="0 0 24 24"
      style={couleur ? { color: couleur } : undefined}
      role={titre ? "img" : "presentation"}
      aria-hidden={titre ? undefined : true}
      aria-label={titre}
    >
      <path d={trace.d} />
    </svg>
  );
}
